// Lote — uma coleta de um Cliente (ticket #9, ADR-0005). Nasce no lançamento e
// encerra numa Devolução. A situação **não** é campo: é derivada dos marcos
// gravados. Funções puras sobre o Snapshot.

import { newId, type Id } from "./ids.ts";
import { isDataISO, type DataISO } from "./datas.ts";
import type { ContagemLote, Lote, Snapshot } from "./snapshot.ts";
import { desfazerSaidasDoSublote, refazerFechamento, saidasDoSublote } from "./devolucao.ts";
import { retidasDeNovoDoLote } from "./backlog.ts";

export type { ContagemLote, Lote };

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

/** A OS vem de um bloco pré-impresso de 6 dígitos, de numeração contínua e única. */
export const OS_MIN = 1;
export const OS_MAX = 999_999;

export class ErroLote extends Error {}

export interface DadosLote {
  clienteId: Id;
  os: number;
  coletaData: DataISO;
  coletaHora: string;
  /** Parada de coleta de origem; `null` = Coleta Avulsa. */
  paradaId: Id | null;
  sacos: number | null;
  urgente: boolean;
  /** Prazo previsto informado à mão ("YYYY-MM-DD HH:MM"); sobrepõe o cálculo. */
  previstoManual: string | null;
  /** OS de relavagem (ADR-0015): não entra na fatura e não traz cobrança. */
  relavagem: boolean;
}

function validar(
  dados: DadosLote,
  snapshot: Snapshot,
  ignorarId?: Id,
): DadosLote {
  const cliente = snapshot.clientes.find((c) => c.id === dados.clienteId);
  if (!cliente) throw new ErroLote("Escolha um cliente.");

  if (!Number.isInteger(dados.os) || dados.os < OS_MIN || dados.os > OS_MAX) {
    throw new ErroLote(`Informe a OS do bloco (${OS_MIN} a ${OS_MAX}).`);
  }
  // A OS vem de um bloco pré-impresso de numeração contínua: é única.
  const repetida = snapshot.lotes.find(
    (l) => l.id !== ignorarId && l.os === dados.os,
  );
  if (repetida) {
    throw new ErroLote(`A OS ${dados.os} já está na OS ${repetida.codigo}.`);
  }
  if (!isDataISO(dados.coletaData)) {
    throw new ErroLote("Informe a data da coleta.");
  }
  // coleta no futuro é aceita (a OS em sequência pode cair num dia de Parada
  // adiante — #88); o que não pode é FECHAR com devolução no futuro (ADR-0028)
  if (!HORA.test(dados.coletaHora)) {
    throw new ErroLote("Hora da coleta inválida (use HH:MM).");
  }
  if (dados.paradaId != null) {
    const parada = snapshot.paradas.find((p) => p.id === dados.paradaId);
    if (!parada || parada.clienteId !== dados.clienteId) {
      throw new ErroLote("A parada escolhida não é deste cliente.");
    }
  }
  const PM = /^\d{4}-\d{2}-\d{2} ([01]\d|2[0-3]):[0-5]\d$/;
  if (dados.previstoManual != null && !PM.test(dados.previstoManual)) {
    throw new ErroLote("Prazo previsto manual inválido (use AAAA-MM-DD HH:MM).");
  }
  if (
    dados.sacos != null &&
    (!Number.isInteger(dados.sacos) || dados.sacos < 0)
  ) {
    throw new ErroLote("Quantidade de sacos inválida.");
  }
  return { ...dados };
}

/**
 * `AAA-2026-0001` — código do Cliente + ano da coleta + maior sequencial
 * daquele Cliente naquele ano + 1. Sem contador persistido (ADR-0005).
 */
export function proximoCodigoLote(
  snapshot: Pick<Snapshot, "lotes" | "clientes">,
  clienteId: Id,
  ano: string,
  ignorarId?: Id,
): string {
  const cliente = snapshot.clientes.find((c) => c.id === clienteId);
  const sigla = cliente?.codigo ?? "???";
  const prefixo = `${sigla}-${ano}-`;
  let maior = 0;
  for (const l of snapshot.lotes) {
    if (l.id === ignorarId || !l.codigo.startsWith(prefixo)) continue;
    const n = Number(l.codigo.slice(prefixo.length));
    if (Number.isInteger(n) && n > maior) maior = n;
  }
  return `${prefixo}${String(maior + 1).padStart(4, "0")}`;
}

export function lancarLote(
  snapshot: Snapshot,
  dados: DadosLote,
  recebidas: { tipoId: Id; quantidade: number; retidaInformada?: number | null }[] = [],
): Snapshot {
  let out = validar(dados, snapshot);
  const lote: Lote = {
    id: newId(),
    clienteId: out.clienteId,
    codigo: proximoCodigoLote(snapshot, out.clienteId, out.coletaData.slice(0, 4)),
    os: out.os,
    coletaData: out.coletaData,
    coletaHora: out.coletaHora,
    paradaId: out.paradaId,
    sacos: out.sacos,
    urgente: out.urgente,
    previstoManual: out.previstoManual,
    relavagem: out.relavagem,
    devolvidoEm: null,
    devolvidoHora: null,
    canceladoEm: null,
    motivoCancelamento: null,
    reabertoEm: null,
    fechamentoAnteriorEm: null,
    fechamentoAnteriorHora: null,
    fechamentoRegistradoEm: null,
    rascunhoFechamento: null,
  };
  let result = { ...snapshot, lotes: [...snapshot.lotes, lote] };
  for (const r of recebidas) {
    result = definirRecebida(result, lote.id, r.tipoId, r.quantidade, r.retidaInformada);
  }
  return result;
}

/** Uma OS da importação: os dados do Lote + a Contagem Recebida por Tipo. */
export interface EntradaLoteMassa {
  dados: DadosLote;
  recebidas: { tipoId: Id; quantidade: number; retidaInformada?: number | null }[];
}

export interface ResultadoLoteMassa {
  importadas: number;
  falhas: { os: number; motivo: string }[];
}

/**
 * Lança várias OS numa transição só (importação de .csv). OS que o domínio
 * recusa (ex.: número repetido) é pulada, com o motivo — as demais entram.
 */
export function lancarLotesEmMassa(
  snapshot: Snapshot,
  entradas: readonly EntradaLoteMassa[],
): { snapshot: Snapshot; resultado: ResultadoLoteMassa } {
  let out = snapshot;
  let importadas = 0;
  const falhas: ResultadoLoteMassa["falhas"] = [];
  for (const e of entradas) {
    try {
      out = lancarLote(out, e.dados, e.recebidas);
      importadas += 1;
    } catch (err) {
      falhas.push({
        os: e.dados.os,
        motivo: err instanceof Error ? err.message : "Não foi possível importar.",
      });
    }
  }
  return { snapshot: out, resultado: { importadas, falhas } };
}

/**
 * Edita um Lote. Se o Cliente ou o ano da coleta mudarem, a numeração interna é
 * refeita — ela carrega os dois.
 */
export function editarLote(
  snapshot: Snapshot,
  id: Id,
  dados: DadosLote,
  recebidas: { tipoId: Id; quantidade: number; retidaInformada?: number | null }[] = [],
): Snapshot {
  const atual = snapshot.lotes.find((l) => l.id === id);
  if (!atual) throw new ErroLote("Lote não encontrado.");
  if (situacaoLote(snapshot, id) === "faturado") {
    throw new ErroLote("Lote já faturado. Reabra a fatura para editar.");
  }
  const d = validar(dados, snapshot, id);
  // Peça retida nesta OS que já saiu do Backlog (carona de outra OS, ou
  // Perda) pertence ao Cliente de hoje: trocar o Cliente deixaria peça de um
  // hotel devolvida a outro (ADR-0028).
  if (d.clienteId !== atual.clienteId) {
    const { lotes, perdas } = saidasDoSublote(snapshot, id);
    if (lotes.length > 0 || perdas > 0) {
      const oss = lotes.map((l) => snapshot.lotes.find((x) => x.id === l)?.os).join(", ");
      throw new ErroLote(
        "Não dá para trocar o cliente: peças retidas nesta OS já saíram do Backlog" +
          (lotes.length > 0 ? ` na OS ${oss}` : "") +
          (perdas > 0 ? `${lotes.length > 0 ? " e" : ""} como perda` : "") +
          ". Desfaça essas saídas antes.",
      );
    }
  }
  const anoNovo = d.coletaData.slice(0, 4);
  const mudouChave =
    d.clienteId !== atual.clienteId || anoNovo !== atual.coletaData.slice(0, 4);
  const codigo = mudouChave
    ? proximoCodigoLote(snapshot, d.clienteId, anoNovo, id)
    : atual.codigo;
  let out = {
    ...snapshot,
    lotes: snapshot.lotes.map((l) => (l.id === id ? { ...l, ...d, codigo } : l)),
  };

  // Garante que os itens previamente contabilizados sejam atualizados,
  // zerando/removendo os ausentes.
  const tiposPresentes = new Set(recebidas.map((r) => r.tipoId));
  const contagensAtuais = contagensDoLote(out, id);
  for (const c of contagensAtuais) {
    if (!tiposPresentes.has(c.tipoId)) {
      out = definirRecebida(out, id, c.tipoId, 0);
    }
  }

  for (const r of recebidas) {
    // a edição manda a Comanda inteira: o relave informado vale como veio
    // (null = apagado), nunca herdado do valor anterior
    out = definirRecebida(out, id, r.tipoId, r.quantidade, r.retidaInformada ?? null);
  }

  // OS já fechada: a Coleta nova precisa refletir no Fechamento (redivide a
  // parte própria e a carona do Backlog contra a Recebida nova)
  return refazerFechamento(snapshot, out, id);
}

/** Apaga de vez. Só enquanto o Lote está **lançado** (ADR-0005). */
export function excluirLote(snapshot: Snapshot, id: Id): Snapshot {
  if (!snapshot.lotes.some((l) => l.id === id)) {
    throw new ErroLote("Lote não encontrado.");
  }
  if (situacaoLote(snapshot, id) !== "lancado") {
    throw new ErroLote(
      "Só dá para excluir um lote ainda lançado. Depois disso, cancele com um motivo.",
    );
  }
  const sem: Snapshot = {
    ...snapshot,
    lotes: snapshot.lotes.filter((l) => l.id !== id),
    contagens: snapshot.contagens.filter((c) => c.loteId !== id),
    retencoes: snapshot.retencoes.filter((r) => r.loteId !== id),
    baixasBacklog: snapshot.baixasBacklog.filter((b) => b.loteId !== id),
    baixasBacklogInicial: snapshot.baixasBacklogInicial.filter((b) => b.loteId !== id),
  };
  // OS reaberta ainda tem o Sublote do fechamento: o que já saiu dele (carona
  // de outras OS, Perdas) é refeito, não deixado órfão (ADR-0026)
  return desfazerSaidasDoSublote(snapshot, sem, id);
}

/**
 * Cancela com motivo obrigatório. Vale de **devolvido** em diante — inclui
 * uma OS **reaberta** (ADR-0024: ela já tem Contagem/Retenção/baixas reais,
 * não é uma OS em branco, então não se exclui de vez, só se cancela) —,
 * antes de faturado; enquanto só lançado, o caminho é excluir (ADR-0005).
 */
export function cancelarLote(
  snapshot: Snapshot,
  id: Id,
  motivo: string,
  em: DataISO,
): Snapshot {
  if (!snapshot.lotes.some((l) => l.id === id)) {
    throw new ErroLote("Lote não encontrado.");
  }
  const situacao = situacaoLote(snapshot, id);
  if (situacao === "cancelado") throw new ErroLote("Lote já cancelado.");
  if (situacao === "faturado") {
    throw new ErroLote("Lote já faturado. Reabra a fatura antes de cancelar.");
  }
  if (situacao === "lancado") {
    throw new ErroLote(
      "Um lote ainda lançado se exclui, não se cancela — não há nada preso a ele.",
    );
  }
  const texto = motivo.trim();
  if (texto === "") throw new ErroLote("Informe o motivo do cancelamento.");

  // O Sublote some; o que já tinha saído dele (carona de outras OS, Perdas)
  // é refeito em vez de ficar órfão ou negativo (ADR-0026).
  const cancelado: Snapshot = {
    ...snapshot,
    // a Devolução deixa de valer: o Sublote some e as baixas voltam ao Backlog
    retencoes: snapshot.retencoes.filter((r) => r.loteId !== id),
    baixasBacklog: snapshot.baixasBacklog.filter((b) => b.loteId !== id),
    baixasBacklogInicial: snapshot.baixasBacklogInicial.filter((b) => b.loteId !== id),
    lotes: snapshot.lotes.map((l) =>
      l.id === id ? { ...l, canceladoEm: em, motivoCancelamento: texto } : l,
    ),
  };
  return desfazerSaidasDoSublote(snapshot, cancelado, id);
}

// ------------------------------------------------------------- contagens ----

/** Define a Contagem Recebida de um Tipo no Lote. Preserva o que já foi devolvido. */
export function definirRecebida(
  snapshot: Snapshot,
  loteId: Id,
  tipoId: Id,
  quantidade: number,
  retidaInformada?: number | null,
): Snapshot {
  if (!snapshot.lotes.some((l) => l.id === loteId)) {
    throw new ErroLote("Lote não encontrado.");
  }
  if (!snapshot.tiposPeca.some((t) => t.id === tipoId)) {
    throw new ErroLote("Tipo de peça não encontrado.");
  }
  if (!Number.isInteger(quantidade) || quantidade < 0) {
    throw new ErroLote("Quantidade inválida.");
  }
  // a Retida digitada é a foto do Backlog do hotel (ADR-0029): contagem de
  // peças, nunca negativa (a produção tinha um -1 gravado, OS 769)
  if (retidaInformada != null && (!Number.isInteger(retidaInformada) || retidaInformada < 0)) {
    throw new ErroLote("Retida inválida: informe um número inteiro de peças, zero ou mais.");
  }
  const atual = snapshot.contagens.find(
    (c) => c.loteId === loteId && c.tipoId === tipoId,
  );
  const devolvida = atual?.devolvida ?? 0;
  const outras = snapshot.contagens.filter(
    (c) => !(c.loteId === loteId && c.tipoId === tipoId),
  );
  // undefined = quem chamou não mexe no relave (preserva); null = apaga
  const retida = retidaInformada === undefined ? (atual?.retidaInformada ?? null) : retidaInformada;
  // linha sem nada em nenhuma das colunas não precisa existir — mas peça que
  // só está no Backlog (Recebida 0) pode ter Retida digitada na comanda, e
  // ela tem de ficar gravada para ser conferida no "Fechar OS" (ADR-0029)
  if (quantidade === 0 && devolvida === 0 && retida == null) {
    return { ...snapshot, contagens: outras };
  }
  // `conferida`/`conferidaMotivo` não são deste comando (só o Fechamento de
  // OS mexe neles, ADR-0021) — preserva o que já havia, MAS só enquanto a
  // Recebida não muda: a Conferida registrada mediu a divergência contra o
  // valor antigo da Recebida, então mudar a Recebida por baixo dela invalida
  // essa divergência — melhor derrubar e pedir pra confirmar de novo
  // (regravando o Fechamento de OS) do que deixar uma divergência velha
  // combinada com uma Recebida nova.
  const recebidaMudou = atual != null && atual.recebida !== quantidade;
  const nova: ContagemLote = {
    loteId,
    tipoId,
    recebida: quantidade,
    devolvida,
    conferida: recebidaMudou ? null : atual?.conferida ?? null,
    conferidaMotivo: recebidaMudou ? null : atual?.conferidaMotivo ?? null,
    retidaInformada: retida,
  };
  return { ...snapshot, contagens: [...outras, nova] };
}

export function contagensDoLote(
  snapshot: Pick<Snapshot, "contagens">,
  loteId: Id,
): ContagemLote[] {
  return snapshot.contagens.filter((c) => c.loteId === loteId);
}

export function totalRecebido(
  snapshot: Pick<Snapshot, "contagens">,
  loteId: Id,
): number {
  return contagensDoLote(snapshot, loteId).reduce((a, c) => a + c.recebida, 0);
}

export function totalDevolvido(
  snapshot: Pick<Snapshot, "contagens">,
  loteId: Id,
): number {
  return contagensDoLote(snapshot, loteId).reduce((a, c) => a + c.devolvida, 0);
}

/** Quanto foi retido de um Tipo neste Lote (todas as linhas de motivo somadas). */
export function retidoNoLote(
  snapshot: Pick<Snapshot, "retencoes">,
  loteId: Id,
  tipoId: Id,
): number {
  return snapshot.retencoes
    .filter((r) => r.loteId === loteId && r.tipoId === tipoId)
    .reduce((a, r) => a + r.quantidade, 0);
}

/**
 * A **Divergência** da Comanda num Tipo: `recebida − devolvida − Σ retida`.
 *
 * Positivo = faltou (o Cliente declarou Peça que não apareceu); negativo =
 * sobrou (veio Peça a mais do que a Comanda dizia). É **informativa** — avisa,
 * nunca bloqueia (ADR-0007). As Peças que voltaram do Backlog não entram nesta
 * conta: vieram de outro Lote.
 */
export function divergencia(
  snapshot: Pick<Snapshot, "contagens" | "retencoes" | "baixasBacklog" | "baixasBacklogInicial">,
  loteId: Id,
  tipoId: Id,
): number {
  const c = snapshot.contagens.find(
    (x) => x.loteId === loteId && x.tipoId === tipoId,
  );
  const recebida = c?.recebida ?? 0;
  const devolvida = c?.devolvida ?? 0;
  // as retidas de novo vieram do Backlog, não desta Comanda (ADR-0025)
  const retidoProprio = retidoNoLote(snapshot, loteId, tipoId) - retidasDeNovoDoLote(snapshot, loteId, tipoId);
  return recebida - devolvida - retidoProprio;
}

/** A Divergência do Lote inteiro, somando todos os Tipos (faltas e sobras se anulam). */
export function totalDivergencia(
  snapshot: Pick<Snapshot, "contagens" | "retencoes" | "baixasBacklog" | "baixasBacklogInicial">,
  loteId: Id,
): number {
  const tipos = new Set([
    ...snapshot.contagens.filter((c) => c.loteId === loteId).map((c) => c.tipoId),
    ...snapshot.retencoes.filter((r) => r.loteId === loteId).map((r) => r.tipoId),
  ]);
  let total = 0;
  for (const t of tipos) total += divergencia(snapshot, loteId, t);
  return total;
}

// -------------------------------------------------------------- situação ----

export type SituacaoLote = "lancado" | "fechado" | "reaberta" | "faturado" | "cancelado";

const ROTULO: Record<SituacaoLote, string> = {
  lancado: "Lançado",
  fechado: "Fechado",
  reaberta: "Reaberta",
  faturado: "Faturado",
  cancelado: "Cancelado",
};

export function rotuloSituacao(s: SituacaoLote): string {
  return ROTULO[s];
}

/**
 * A situação é **calculada**, nunca guardada: os marcos gravados
 * (`canceladoEm`, `devolvidoEm`, `reabertoEm`) e o vínculo com uma Fatura
 * emitida (ADR-0005; `reaberta` é ADR-0024). "faturado" vence "reaberta" de
 * propósito: uma OS reaberta que volta a ser faturada não precisa apagar
 * `reabertoEm` pra sumir de vista — só perde a prioridade no cálculo.
 */
export function situacaoLote(snapshot: Snapshot, loteId: Id): SituacaoLote {
  const lote = snapshot.lotes.find((l) => l.id === loteId);
  if (!lote) throw new ErroLote("Lote não encontrado.");
  if (lote.canceladoEm) return "cancelado";
  if (loteEstaFaturado(snapshot, loteId)) return "faturado";
  // sem Fechamento gravado é Lançada — inclusive a OS de Fatura reaberta
  // cujo Fechamento também foi reaberto para correção
  if (!lote.devolvidoEm) return "lancado";
  if (lote.reabertoEm) return "reaberta";
  return "fechado";
}

/** O Lote entrou numa Fatura já emitida (ticket #19). */
export function loteEstaFaturado(
  snapshot: Pick<Snapshot, "faturas">,
  loteId: Id,
): boolean {
  return snapshot.faturas.some(
    (f) => f.emitidaEm != null && f.loteIds.includes(loteId),
  );
}

// --------------------------------------------------------------- coleta ----

/** Data/hora da coleta como `Date` local. */
export function coletaEm(lote: Pick<Lote, "coletaData" | "coletaHora">): Date {
  const [y, m, d] = lote.coletaData.split("-").map(Number);
  const [hh, mm] = lote.coletaHora.split(":").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0, 0);
}
